import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, MessageCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useBluelagoon } from "@bluelagoon";
import ChatHistoryRowPreview from "./ChatHistoryRowPreview";

export default function ChatHistoryRow({
    chat,
    isCurrentSelection,
    isExpanded,
    onToggleExpand,
    onOpen,
}) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState(null);
    const { call: fetchPreview, isLoading } = useBluelagoon({
        method: "getStepChatPreview",
    });

    useEffect(() => {
        if (!isExpanded || messages !== null) return undefined;
        let cancelled = false;
        fetchPreview({ stepChatId: chat.id, limit: 5 })
            .then((res) => !cancelled && setMessages(res?.messages || []))
            .catch(() => !cancelled && setMessages([]));
        return () => {
            cancelled = true;
        };
    }, [isExpanded, messages, chat.id, fetchPreview]);

    const timeAgo = useMemo(() => {
        try {
            return formatDistanceToNow(new Date(chat.createdAt), { addSuffix: true });
        } catch {
            return "";
        }
    }, [chat.createdAt]);

    const title = chat.isActive
        ? t("chatTwoColumnPage.currentSession")
        : timeAgo || new Date(chat.createdAt).toLocaleString();

    return (
        <li className={isCurrentSelection ? "bg-blue-50/60" : "hover:bg-gray-50"}>
            <button
                onClick={onToggleExpand}
                className="w-full text-left px-4 py-3 flex items-start gap-3"
            >
                <MessageCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                            {title}
                        </span>
                        {chat.isActive && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-semibold">
                                {t("chatTwoColumnPage.activeBadge")}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                        {chat.preview || t("chatTwoColumnPage.noPreview")}
                    </p>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                    }`}
                />
            </button>
            {isExpanded && (
                <ChatHistoryRowPreview
                    messages={messages}
                    isLoading={isLoading}
                    onOpen={onOpen}
                />
            )}
        </li>
    );
}
